<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { iTitleBanner, iSpinner } from '@inscribe/signal';
import { encodeQueryParams, getAndSyncQueryParams } from '@/utils/queryParams';
import DatesFilter from '@/V2/components/filters/DatesFilter';
import TagsFilter from '@/V2/components/filters/TagsFilter';
import DocumentTypesFilter from '@/V2/components/filters/DocumentTypesFilter';
import FileTypesFilter from '@/V2/components/filters/FileTypesFilter';
import CustomerStatusesFilter from '@/V2/components/filters/CustomerStatusesFilter';
import ChartCard from '@/V2/analytics/ChartCard';
import CustomerFraudRatingCard from '@/V2/analytics/CustomerFraudRatingCard';
import {
  InscribeAnalyticsBluePalette,
  InscribeAnalyticsRedPalette,
} from './../analytics/utils';
import EmptyPage from '@/shared/components/EmptyPage';

export default {
  components: {
    iSpinner,
    iTitleBanner,
    DatesFilter,
    TagsFilter,
    DocumentTypesFilter,
    FileTypesFilter,
    CustomerStatusesFilter,
    ChartCard,
    CustomerFraudRatingCard,
    EmptyPage,
  },
  data() {
    return {
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      isLoading: false,
      data: undefined,
      error: undefined,
      fetchAbortController: undefined,
      InscribeAnalyticsBluePalette,
      InscribeAnalyticsRedPalette,
    };
  },
  computed: {
    ...mapGetters(['isOrgAdvancedUserOrAdmin']),
    numberOfCustomers() {
      return this.data?.number_of_customers.toLocaleString() ?? '';
    },
    numberOfDocuments() {
      return this.data?.number_of_documents.toLocaleString() ?? '';
    },
    numberOfFlaggedDocuments() {
      return this.data?.number_of_flagged_documents.toLocaleString() ?? '';
    },
    numberOfHighRiskCustomers() {
      return this.data?.number_of_high_risk_customers.toLocaleString() ?? '';
    },
    typeOfDocsCount() {
      return this.data?.file_type_breakdown.length;
    },
    trustScoreWithPadding() {
      return Array.from(Array(101).keys()).map((i) => {
        const foundIndex = this.data.trust_score_breakdown.findIndex(
          (d) => d.bucket === i,
        );
        return foundIndex > -1
          ? this.data.trust_score_breakdown[foundIndex]
          : {
              bucket: i,
              count: 0,
            };
      });
    },
    flagRate() {
      if (!this.data) {
        return '';
      }
      if (this.data.number_of_documents === 0) {
        return '0%';
      }
      const raw =
        this.data.number_of_flagged_documents / this.data.number_of_documents;
      return `${Math.round(raw * 1000) / 10}%`;
    },
    highRiskCustomerRate() {
      if (!this.data) {
        return '';
      }
      if (this.data.number_of_customers === 0) {
        return '0%';
      }
      const raw =
        this.data.number_of_high_risk_customers / this.data.number_of_customers;
      return `${Math.round(raw * 1000) / 10}%`;
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
    },
    params: {
      handler: 'fetchData',
      immediate: true,
    },
  },
  methods: {
    async fetchData() {
      // if there is a previous inflight request, cancel it
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
      }
      this.fetchAbortController = new AbortController();

      this.isLoading = true;
      try {
        const res = await axios.get(
          `/api/v2/analytics?${encodeQueryParams(this.params)}`,
          {
            signal: this.fetchAbortController.signal,
          },
        );
        this.data = res.data.data;
      } catch (e) {
        if (e.message !== 'canceled') {
          this.error = e;
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="analytics-page__wrapper">
    <iTitleBanner
      title="Analytics"
      subtitle="Insights into overall fraud detection and performance"
      icon-name="large-graph-2"
      data-testid="analytics-page-title"
      class-name="analytics-page__title-banner"
    />

    <!-- missing permissions message-->
    <empty-page
      v-if="!isOrgAdvancedUserOrAdmin"
      icon="large-lock"
      title="Permission Required"
      message="You do not have sufficient permissions to access this page. Please ask your team admin to give you access."
    ></empty-page>

    <div v-else class="analytics-page__content">
      <div
        :key="isLoading"
        :class="{ 'is-loading': isLoading }"
        class="analytics-page__filters"
      >
        <DatesFilter />
        <TagsFilter />
        <DocumentTypesFilter />
        <FileTypesFilter />
        <CustomerStatusesFilter entity="analytics" />
        <iSpinner v-if="isLoading" size="sm" type="neutral" />
      </div>

      <div class="analytics-page__metrics">
        <div class="analytics-page__metric i-radius-2xl">
          <div class="analytics-page__metric-subheading">Customers</div>
          <h4 data-testid="analytics-page-customers-metric">
            {{ numberOfCustomers }}
          </h4>
        </div>
        <div class="analytics-page__metric i-radius-2xl">
          <div class="analytics-page__metric-subheading">
            High Risk Customers
          </div>
          <h4 data-testid="analytics-page-flag-rate-metric">
            {{ numberOfHighRiskCustomers }}
          </h4>
        </div>
        <div class="analytics-page__metric i-radius-2xl">
          <div class="analytics-page__metric-subheading">High Risk Rate</div>
          <h4 data-testid="analytics-page-flag-rate-metric">
            {{ highRiskCustomerRate }}
          </h4>
        </div>
        <div class="analytics-page__metric i-radius-2xl">
          <div class="analytics-page__metric-subheading">Documents</div>
          <h4 data-testid="analytics-page-documents-metric">
            {{ numberOfDocuments }}
          </h4>
        </div>
        <div class="analytics-page__metric i-radius-2xl">
          <div class="analytics-page__metric-subheading">Flagged Documents</div>
          <h4 data-testid="analytics-page-flagged-documents-metric">
            {{ numberOfFlaggedDocuments }}
          </h4>
        </div>
        <div class="analytics-page__metric i-radius-2xl">
          <div class="analytics-page__metric-subheading">Flag Rate</div>
          <h4 data-testid="analytics-page-flag-rate-metric">
            {{ flagRate }}
          </h4>
        </div>
      </div>

      <div
        v-if="data"
        class="analytics-page__charts d-flex flex-column i-gap-8"
      >
        <div class="analytics-page__chart-row">
          <customer-fraud-rating-card
            data-testid="analytics-page-customer-fraud-rating-card"
            :data="data.customer_fraud_rating_breakdown"
            :total="data.number_of_customers"
          ></customer-fraud-rating-card>
        </div>
        <div class="analytics-page__chart-row">
          <chart-card
            title="Flagged Documents"
            subtitle="Breakdown of flagged workspace documents by type"
            data-testid="analytics-page-flagged-documents-chart"
            :data="data.flagged_document_breakdown"
            :total="data.number_of_flagged_documents"
            :legend-clickable="true"
            :links-extra-params="{ is_fraudulent: true }"
            :chart-palette="InscribeAnalyticsRedPalette"
            entity="docs"
            size="lg"
          ></chart-card>
        </div>
        <div class="analytics-page__chart-row">
          <chart-card
            title="All Document Types"
            subtitle="Breakdown of workspace documents by type"
            :data="data.document_type_breakdown"
            :total="data.number_of_documents"
            :legend-clickable="true"
            :chart-palette="InscribeAnalyticsBluePalette"
            entity="docs"
          ></chart-card>
          <chart-card
            title="All File Types"
            subtitle="Breakdown of workspace file formats"
            :data="data.file_type_breakdown"
            :total="typeOfDocsCount"
            :legend-clickable="true"
            :chart-palette="InscribeAnalyticsBluePalette"
            entity="file-types"
          ></chart-card>
        </div>
        <div class="analytics-page__chart-row">
          <chart-card
            :data="trustScoreWithPadding"
            title="Trust Score Distribution"
            subtitle="Distribution of trust scores across workspace documents"
            entity="trust-score"
            size="full"
          ></chart-card>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.analytics-page {
  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: map-get($increments, 11) map-get($increments, 12);
    max-width: $desktop;
    margin: auto;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;

    h1 {
      margin: 0;
    }
  }

  &__charts {
    width: 100%;
  }

  &__chart-row {
    display: flex;
    flex-direction: row;
    gap: map-get($increments, 8);
    width: 100%;

    @include respond-to('medium') {
      flex-direction: column;
    }
  }

  &__filters {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    gap: map-get($increments, 3);
    margin: map-get($increments, 11) 0;

    &.is-loading {
      pointer-events: none;
    }
  }

  &__metrics {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: map-get($increments, 8);
    width: 100%;
    margin-bottom: map-get($increments, 8);

    @include respond-to('small') {
      flex-direction: column;
    }
  }

  &__metric {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    height: 100%;
    gap: map-get($increments, 2);
    padding: map-get($increments, 9);
    border: 1px solid $light-neutral-60;
    border-radius: map-get($increments, 3);
    min-height: 93px;

    @include respond-to('medium') {
      min-height: 111px;
    }

    @include respond-to('small') {
      min-height: 93px;
    }

    h4 {
      margin-bottom: 0;
    }
  }

  &__metric-subheading {
    color: $light-neutral-110;
    font-size: map-get($text-size-standard, 3);
    font-weight: map-get($text-weights, 'medium');
  }
}
</style>
